<template>
    <PageStyleComponent :loader="loader" @showLoader="showLoader" @hideLoader="hideLoader">
        <template v-slot:body>
            <div class="fixed bg-white w-[93%] z-60" v-if="pageTitle != ''">
                <p class="font-bold text-lg text-black">{{ pageTitle }}</p>
            </div>
            <div class="fixed bg-white w-[97.5%] z-50">
                <FilterBar 
                    :addButtonLabel="addButtonLabel" 
                    :showAddButton="showAddButton"
                    :filters="searchFilters" 
                    @add-new="handleAddNew"
                    @search="searchPage"
                    @reset="resetFilters"
                    @importData="importData"
                    @removeItem="removeItem"
                    @removeSelectedItems="removeSelectedItems"
                    @printList="printList"
                    @printExcel="printExcel"
                    @printCSV="printCSV"
                    :dropdownOptions="dropdownOptions"
                    @handleDynamicOption="handleDynamicOption"
                    :options="options"
                    :addingRight="addingRight"
                    :removingRight="removingRight"
                    :rightsModule="rightsModule"
                    :dropdownWidth="dropdownWidth"
                    :selectOptions="selectOptions"
                    :updateValue="updateValue"
                    :searchPlaceholder="searchPlaceholder"
                    v-model:printModalVisible="modalVisibleProxy"
                    :printTitle="printTitle"
                    :pdfUrl="pdfUrl"
                    />
            </div>
            <div class="fixed table w-[97.5%] top-[9.9rem] z-20">
                <DynamicTable 
                    ref="dynamicTable"
                    :columns="columns" 
                    :rows="rows"
                    :idField="idField"
                    :actions="actions"
                    :showTotals="showTotals"
                    :showActions="showActions"
                    :groupingKey="groupingKey"
                    :groupingParentKey="groupingParentKey"
                    :enableContextMenu="enableContextMenu"
                    @action-click="handleActionClick"
                    @row-db-click="handleShowDetails"
                    @link-db-click="handleOpenLink"
                    @right-click="handleRightClick"
                    @selection-changed="handleSelectionChange"
                    :rightsModule="rightsModule"
                />
            </div>
            <div class="fixed w-[98%] z-30 bottom-5 pb-2 bg-white">
                <transition name="slide-up">
                    <ShowDetails 
                        v-model:visible="showDetails"
                        :detailsTitle="detailsTitle"
                        @hideDetails="hideDetails"
                    >
                        <template v-slot:detailSlot>
                            <slot></slot>
                        </template>
                    </ShowDetails>
                </transition>
                <MyPagination 
                :count="count"
                :currentPage="currentPage"
                :result="result"
                @loadPrev="loadPrev"
                @loadNext="loadNext"
                @firstPage="firstPage"
                @lastPage="lastPage"
                @selectSearchQuantity="selectSearchQuantity"
                :showNextBtn="showNextBtn"
                :showPreviousBtn="showPreviousBtn"
            
            />
            </div>
        </template>
    </PageStyleComponent>

</template>

<script>
import { defineComponent, ref, computed} from 'vue';
import FilterBar from '@/components/FilterBar.vue'
import Loader from '@/components/Loader.vue'
import DynamicTable from '@/components/DynamicTable.vue'
import MyPagination from '@/components/MyPagination.vue'
import PageStyleComponent from './PageStyleComponent.vue';
import ShowDetails from '@/components/ShowDetails.vue';

export default defineComponent({
    props:{
        showDetails: {
            type: Boolean,
            default: false
        },
        detailsTitle:{
            type: String,
            required: false
        },
        pageTitle:{
            type: String,
            required: false,
            default: () => ''
        },
        addButtonLabel:{
            type: String,
            required: true
        },
        showAddButton: {
            type: Boolean,
            default: true,
            required: false
        },
        dropdownOptions: {
            type: Array,
            default: () => []
        },
        printModalVisible:{
            type: Boolean,
            required: true
        },
        printTitle:{
            type: String,
            default: 'Print'
        },
        pdfUrl:{
            type: String,
            default: ''
        },
        searchFilters:{
            type: Array,
            default: () => []   
        },
        columns:{
            type: Array,
            default: () => []   
        },
        rows:{
            type: Array,
            default: () => []   
        },
        showTotals:{
            type: Boolean,
            default: () => false,
            required: false
        },
        showActions:{
            type: Boolean,
            default: () => true,
            required: false
        },
        actions:{
            type: Array,
            default: () => []   
        },
        idField: {
            type: String,
            default: () => ''
        },
        count:{
            type: Number,
            default: () => 1   
        },
        selectedValue: {
            type: Number,
            required: true,
            default: () => 50
        },
        currentPage:{
            type: Number,
            default: () => 1   
        },
        result:{
            type: Array,
            default: () => [] 
        },
        showNextBtn:{
            type: Boolean,
            default: () => false  
        },
        showPreviousBtn:{
            type: Boolean,
            default: () => false    
        },
        options: {
            type: Array,
            default: () => []
        },
        selectOptions: {
            type: Array,
            default: () => []
        },
        updateValue: {
            type: String,
            default: () => ''
        },
        dropdownWidth: {
            type: String,
            default: () => '300px'
        },
        searchPlaceholder: {
            type: String,
            default: () => 'Select Option...'
        },
        loader:{
            type: String,
            default: 'none'
        },
        rightsModule:{
            type: String,
            default: () => ''
        },
        groupingKey: {
            type: String,
            default: null,
        },
        groupingParentKey: {
            type: String,
            default: null,
        },
        enableContextMenu: {
            type: Boolean,
            default: false
        },
        addingRight:{
            type: String,
            default: () => ''
        },
        removingRight:{
            type: String,
            default: () => ''
        },

    },
    emits: ['update:printModalVisible'],
    components:{
        FilterBar, DynamicTable, MyPagination, Loader, PageStyleComponent,ShowDetails
    },
    setup(props, { emit }){
        const selectedValue = ref(50);
        const dynamicTable = ref(null);

        const searchPage = () =>{
            dynamicTable.value?.clearSelection();
            emit('searchPage')
        }
        const resetFilters = () =>{
            dynamicTable.value?.clearSelection();
            emit('resetFilters')
        }
        const importData = () =>{
            emit('importData');
        }
        const removeItem = () =>{
            emit('removeItem');
        }
        const removeSelectedItems = () =>{
            emit('removeSelectedItems');
        }
        const handleDynamicOption = (option) =>{
            emit('handleDynamicOption', option);
        }
        const printList = () =>{
            emit('printList');
        };
        const printExcel = () =>{
            emit('printExcel');
        };
        const printCSV = () =>{
            emit('printCSV');
        };
        const loadPrev = () =>{
            emit('loadPrev')
        }
        const loadNext = () =>{
            emit('loadNext')
        }
        const firstPage = () =>{
            emit('firstPage')
        }
        const lastPage = () =>{
            emit('lastPage');
        };const selectSearchQuantity = (newValue) =>{
            selectedValue.value = newValue;
            emit("selectSearchQuantity", selectedValue.value);
        };
        const handleActionClick = (rowIndex, action, row) =>{
            emit('handleActionClick',rowIndex, action, row)
        };
        const handleRightClick = (row, rowIndex, event) =>{
            emit('handleRightClick',row, rowIndex, event)
        };
        const handleShowDetails = (row) =>{
            emit('handleShowDetails',row)
        };
        const handleOpenLink = (row) =>{
            emit('handleOpenLink', row)
        }
        const handleAddNew = () =>{
            emit('handleAddNew');
        }
        const showLoader = () =>{
            emit('showLoader');
        }
        const hideLoader = () =>{
            emit('hideLoader');
        }
        const handleSelectionChange = (selectedIds) =>{
            emit('handleSelectionChange', selectedIds);
        };
        const hideDetails = () =>{
            emit('hideDetails');
        };
        const modalVisibleProxy = computed({
            get: () => props.printModalVisible,
            set: (val) => emit('update:printModalVisible', val)
        });
        return{
            searchPage, resetFilters, loadPrev, loadNext, firstPage, lastPage, handleActionClick, handleAddNew,
            showLoader, hideLoader, importData, removeItem, removeSelectedItems, printList, handleDynamicOption,
            handleSelectionChange,selectSearchQuantity,selectedValue,printExcel,printCSV,hideDetails,handleShowDetails,
            handleRightClick,handleOpenLink,modalVisibleProxy,dynamicTable
        }
    }
})
</script>

<style scoped>
.subsection{
    min-height: 100vh;
}
.table{
    min-height: 20vh;
    max-height: 20vh;
    overflow-y: scroll;
    overflow-x: scroll;
}
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.5s ease, opacity 0.5s ease;
  transform-origin: bottom center; /* Animate from bottom */
  min-height: 300px;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: scaleY(0);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave-from {
  transform: scaleY(1);
  opacity: 1;
}
</style>